import React, { ReactElement, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import Select from '@objects/formfields/select'
import {
  BUNDESLAND_REGION_IDS,
  DEUTSCHLAND_REGION_ID,
  REGION_NAMES,
  AccidentAtlasRegionId,
  isAccidentAtlasRegionId,
} from '../types/region'

export type BundeslandSelectProps = RvG.IReactDefaultProps & {
  value?: AccidentAtlasRegionId
  onChange?: (value: AccidentAtlasRegionId) => void
}

type SelectOption = {
  label: string
  value: AccidentAtlasRegionId
}

export default function BundeslandSelect({
  className,
  value,
  onChange,
}: BundeslandSelectProps): ReactElement {
  const intl = useIntl()
  const [menuIsOpen, setMenuIsOpen] = useState(false)

  const options = useMemo<SelectOption[]>(
    () => [
      {
        label: intl.formatMessage({
          id: 'accidentAtlas.bundesland.deutschland',
        }),
        value: DEUTSCHLAND_REGION_ID,
      },
      ...BUNDESLAND_REGION_IDS.map((id) => ({
        label: REGION_NAMES[id],
        value: id,
      })).sort((a, b) => a.label.localeCompare(b.label, 'de')),
    ],
    [intl]
  )

  const selectedOption = value
    ? options.find((option) => option.value === value) ?? null
    : null

  return (
    <Select
      className={className}
      onChange={(option: SelectOption | null) => {
        if (option && isAccidentAtlasRegionId(option.value)) {
          onChange?.(option.value)
        }
      }}
      settings={{
        settingsTheme: 'dark',
        options,
        value: selectedOption,
        'aria-label': intl.formatMessage({
          id: 'accidentAtlas.bundesland.select.aria.label',
        }),
        placeholder: intl.formatMessage({
          id: menuIsOpen
            ? 'accidentAtlas.bundesland.placeholderOpen'
            : 'accidentAtlas.bundesland.placeholder',
        }),
        onMenuOpen: () => setMenuIsOpen(true),
        onMenuClose: () => setMenuIsOpen(false),
        autoFocus: false,
      }}
    />
  )
}
